import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Target, 
  BarChart3, 
  CheckCircle2,
  Sparkles,
  Award,
  LogIn,
  ArrowDown
} from 'lucide-react';
import styles from './Onboarding.module.css';

interface OnboardingSection {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  color: string;
}

const onboardingSections: OnboardingSection[] = [
  {
    id: 'welcome',
    title: 'Bem-vindo ao Axioma Hábitos! 🌱',
    description: 'Transforme pequenas ações em grandes conquistas. Acreditamos que o sucesso e o bem-estar duradouros nascem de um conjunto de verdades fundamentais: os seus hábitos.',
    icon: <Sparkles size={80} />,
    features: [
      'Construa uma vida mais intencional e produtiva',
      'Baseado nos princípios de "Hábitos Atômicos"',
      'Pequenas mudanças, resultados extraordinários',
      'Seus hábitos como axiomas pessoais'
    ],
    color: '#667eea'
  },
  {
    id: 'principles',
    title: 'Princípios Fundamentais ⚡',
    description: 'Identifique e estabeleça os hábitos essenciais que são a base para qualquer melhoria significativa em sua vida, transformando-os em seus próprios "axiomas" pessoais.',
    icon: <Target size={80} />,
    features: [
      'Hábitos como verdades fundamentais',
      'Base sólida para mudanças duradouras',
      'Axiomas personalizados para seu crescimento',
      'Foco nos hábitos que realmente importam'
    ],
    color: '#3B82F6'
  },
  {
    id: 'consistency',
    title: 'Consistência Descomplicada 🎯',
    description: 'Facilitamos a prática diária dessas pequenas ações, tornando-as automáticas e inquebráveis. A construção de bons hábitos se torna tão natural quanto uma verdade autoevidente.',
    icon: <CheckCircle2 size={80} />,
    features: [
      'Rotinas simples e sustentáveis',
      'Hábitos automáticos e inquebráveis',
      'Interface intuitiva para uso diário',
      'Processo natural e sem fricção'
    ],
    color: '#10B981'
  },
  {
    id: 'progress',
    title: 'Progresso Visível 📊',
    description: 'Acompanhe como a aplicação consistente desses "axiomas" em sua rotina leva a um progresso contínuo e resultados exponenciais, mesmo que pareçam pequenos no início.',
    icon: <BarChart3 size={80} />,
    features: [
      'Visualize o poder do progresso composto',
      'Gráficos detalhados de evolução',
      'Celebre cada pequena vitória',
      'Resultados exponenciais ao longo do tempo'
    ],
    color: '#F59E0B'
  },
  {
    id: 'transformation',
    title: 'Construa Seus Pilares 🏆',
    description: 'Você não está apenas acompanhando tarefas, você está construindo os pilares de uma vida mais plena e alinhada com seus objetivos. Comece hoje a plantar seus "axiomas".',
    icon: <Award size={80} />,
    features: [
      'Vida mais plena e intencional',
      'Alinhamento com seus objetivos',
      'Fundamentos sólidos para o crescimento',
      'Colha as grandes conquistas do amanhã'
    ],
    color: '#8B5CF6'
  }
];

interface OnboardingProps {
  onComplete: () => void;
}

const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
  const [currentSection, setCurrentSection] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const sectionIndex = Math.floor(scrollPosition / windowHeight);
      
      if (sectionIndex >= 0 && sectionIndex < onboardingSections.length) {
        setCurrentSection(sectionIndex);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToNext = () => {
    const nextSection = Math.min(currentSection + 1, onboardingSections.length - 1);
    window.scrollTo({
      top: nextSection * window.innerHeight,
      behavior: 'smooth'
    });
  };

  return (
    <div className={styles.scrollContainer}>
      {/* Header fixo com botão de login */}
      <div className={styles.fixedHeader}>        <div className={styles.headerContent}>
          <h1 className={styles.logo}>Axioma Hábitos</h1>
          <button
            onClick={onComplete}
            className={styles.loginButton}
          >
            <LogIn size={20} />
            Fazer Login
          </button>
        </div>
      </div>

      {/* Indicador de progresso lateral */}
      <div className={styles.progressIndicator}>
        {onboardingSections.map((_, index) => (
          <div
            key={index}
            className={`${styles.progressDot} ${
              index === currentSection ? styles.progressDotActive : ''
            }`}
            style={{
              backgroundColor: index === currentSection ? onboardingSections[currentSection].color : '#cbd5e1'
            }}
          />
        ))}
      </div>

      {/* Seções do onboarding */}
      {onboardingSections.map((section, index) => (
        <motion.div
          key={section.id}
          className={styles.section}
          style={{
            background: `linear-gradient(135deg, ${section.color}15 0%, ${section.color}05 100%)`
          }}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className={styles.sectionContent}>
            {/* Ícone principal */}
            <motion.div
              className={styles.sectionIcon}
              initial={{ scale: 0, rotate: -180 }}
              whileInView={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              style={{ color: section.color }}
            >
              {section.icon}
            </motion.div>

            {/* Conteúdo textual */}
            <div className={styles.sectionText}>
              <motion.h2
                className={styles.sectionTitle}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                viewport={{ once: true }}
              >
                {section.title}
              </motion.h2>

              <motion.p
                className={styles.sectionDescription}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                viewport={{ once: true }}
              >
                {section.description}
              </motion.p>

              {/* Features */}
              <motion.div
                className={styles.featuresList}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 1 }}
                viewport={{ once: true }}
              >
                {section.features.map((feature, featureIndex) => (
                  <motion.div
                    key={featureIndex}
                    className={styles.featureItem}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 1.2 + featureIndex * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <CheckCircle2 
                      size={20} 
                      className={styles.featureIcon}
                      style={{ color: section.color }}
                    />
                    <span>{feature}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>

          {/* Botão para próxima seção (exceto na última) */}
          {index < onboardingSections.length - 1 && (
            <motion.button
              className={styles.nextSectionButton}
              onClick={scrollToNext}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.5 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowDown size={24} />
            </motion.button>
          )}

          {/* Call to action na última seção */}
          {index === onboardingSections.length - 1 && (
            <motion.div
              className={styles.ctaSection}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.5 }}
              viewport={{ once: true }}
            >              <button
                onClick={onComplete}
                className={styles.ctaButton}
                style={{ backgroundColor: section.color }}
              >
                <LogIn size={24} />
                Plantar Meus Axiomas
              </button>
            </motion.div>
          )}
        </motion.div>
      ))}
    </div>
  );
};

export default Onboarding;
